/**
 * The timeline of one vulnerability case, laid out like the Art. 14 graphic of
 * epic DEV-61: initial assessment → X → X + 24 h → X + 72 h, Y → Y + 14 days,
 * then customer information, advisory releases and every milestone in order.
 */
import { msg, t } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Chip, { type ChipTone } from '../../components/Chip';
import { Icon } from '../../components/Icon';
import { fmtDate } from '../../lib/format';
import type { CaseClock, TimelineCase } from '../../lib/types';
import { clockChip } from '../triage/CasePanel';

import { hoursLabel, milestoneLabel } from './timelineModel';
import { backendLabel, backendLabelList } from '../../lib/backendLabels';

interface Step {
  key: string;
  title: string;
  chip?: { label: string; tone: ChipTone };
  lines: string[];
}

/** Translated at call time (global i18n); called while CaseTimelineDetail renders. */
function clockStep(row: TimelineCase, clock: CaseClock, title: string): Step {
  const missingItems = row.stages.find((stage) => stage.stage === clock.stage)?.missing ?? [];
  const due = clock.due ? fmtDate(clock.due) : null;
  const lines = [due ? t`fällig ${due}` : t`Frist beginnt mit der Abhilfemaßnahme`];
  if (clock.submitted_at) {
    const submittedAt = fmtDate(clock.submitted_at);
    const submittedBy = clock.submitted_by ?? t`unbekannt`;
    lines.push(t`gemeldet ${submittedAt} · ${submittedBy}`);
  } else if (missingItems.length > 0) {
    const missing = backendLabelList(missingItems);
    lines.push(t`fehlt noch: ${missing}`);
  }
  return { key: clock.key, title, chip: clockChip(clock), lines };
}

function steps(row: TimelineCase): Step[] {
  const clock = (key: CaseClock['key']): CaseClock | undefined => row.clocks.find((c) => c.key === key);
  const early = clock('24h');
  const notification = clock('72h');
  const final = clock('14d');
  const available = row.corrective_measure.available_at;
  const firstHit = row.first_hit_at ? fmtDate(row.first_hit_at) : null;
  const triageDuration = row.triage_hours === null ? null : hoursLabel(row.triage_hours);
  const availableSince = available ? fmtDate(available) : null;
  return [
    {
      key: 'triage',
      title: t`Eingang und Erstprüfung`,
      lines: [
        firstHit ? t`KEV-Treffer ${firstHit}` : t`KEV-Treffer unbekannt`,
        triageDuration === null ? t`Frist läuft noch nicht` : t`Kenntnis nach ${triageDuration}`,
      ],
    },
    { key: 'aware', title: t`X · Kenntnis`, lines: [fmtDate(row.aware_since), t`Betroffen bestätigt`] },
    ...(early ? [clockStep(row, early, t`X + 24 h · Frühwarnung`)] : []),
    ...(notification ? [clockStep(row, notification, t`X + 72 h · Meldung`)] : []),
    {
      key: 'corrective',
      title: t`Y · Abhilfemaßnahme`,
      chip: available ? { label: t`Verfügbar`, tone: 'good' } : { label: t`Noch nicht erfasst`, tone: 'idle' },
      lines: [availableSince ? t`seit ${availableSince}` : t`Update oder Workaround für die Nutzer`],
    },
    ...(final ? [clockStep(row, final, t`Y + 14 Tage · Abschlussbericht`)] : []),
  ];
}

export default function CaseTimelineDetail({ id, row }: Readonly<{ id: string; row: TimelineCase }>): ReactNode {
  const { i18n } = useLingui();
  const { mails } = row;
  const { cve } = row;
  const product = row.product_name ?? '';
  const { sent, total } = mails;
  const firstSent = mails.first_sent_at ? fmtDate(mails.first_sent_at) : null;
  const lastSent = mails.last_sent_at ? fmtDate(mails.last_sent_at) : null;
  const eventCount = row.milestones.length;
  return (
    <div id={id} className="tl-detail">
      <ol className="tl-steps" aria-label={i18n._(msg`Meldeprozess ${cve} · ${product}`)}>
        {steps(row).map((step) => (
          <li key={step.key} className={`tl-step${step.chip ? ` tl-step-${step.chip.tone}` : ''}`}>
            <span className="tl-step-dot" aria-hidden="true" />
            <strong>{step.title}</strong>
            {step.chip ? <Chip tone={step.chip.tone}>{step.chip.label}</Chip> : null}
            {step.lines.map((line) => <span key={line} className="hint">{line}</span>)}
          </li>
        ))}
      </ol>

      <dl className="detail-list tl-facts">
        <dt><Trans>Kundeninformation</Trans></dt>
        <dd>
          {total === 0 ? <Trans>keine Benachrichtigung angelegt</Trans> : <Trans>{sent} von {total} versendet</Trans>}
          {firstSent ? <> · <Trans>erste {firstSent}</Trans></> : null}
          {lastSent ? <> · <Trans>letzte {lastSent}</Trans></> : null}
        </dd>
        <dt>Security Advisory</dt>
        <dd>
          {row.advisories.length === 0
            ? <Trans>noch nicht freigegeben</Trans>
            : row.advisories.map((a) => `${a.version ?? '?'}: ${fmtDate(a.at)}`).join(' · ')}
        </dd>
        {row.srp_reference ? (
          <>
            <dt><Trans>SRP-Referenz</Trans></dt>
            <dd className="mono">{row.srp_reference}</dd>
          </>
        ) : null}
      </dl>

      <details className="triage-context-details">
        <summary><Trans>Alle Ereignisse ({eventCount})</Trans></summary>
        <ol className="tl-milestones">
          {row.milestones.map((milestone, index) => (
            <li key={`${milestone.kind}-${index}`}>
              <span className="muted">{fmtDate(milestone.at)}</span> {milestoneLabel(milestone)}
            </li>
          ))}
        </ol>
      </details>

      {row.event_id !== null ? <TriageLinks row={row} eventId={row.event_id} /> : null}
    </div>
  );
}

/** Into the case tab of the triage; the report of the next open stage right away (DEV-60). */
function TriageLinks({ row, eventId }: Readonly<{ row: TimelineCase; eventId: number }>): ReactNode {
  const base = `/triage?event=${eventId}&tab=case${row.versions[0] ? `&version=${row.versions[0].version_id}` : ''}`;
  const next = row.status === 'OBSOLETE' ? undefined : row.clocks
    .filter((clock) => !clock.submitted_at && clock.due)
    .sort((a, b) => (a.due ?? '').localeCompare(b.due ?? ''))[0];
  const nextLabel = next === undefined ? undefined : backendLabel(next.label);
  return (
    <div className="tl-links">
      {next ? (
        <Link className="link-button" to={`${base}&report=${next.stage}`}>
          <Trans>{nextLabel} vorbereiten</Trans> <Icon name="chevron-right" size={12} />
        </Link>
      ) : null}
      <Link className="link-button" to={base}>
        <Trans>In der Triage bearbeiten</Trans> <Icon name="chevron-right" size={12} />
      </Link>
    </div>
  );
}
